// ============================================================
// session-recovery.js
// Stale-MSAL-session recovery helpers (2026-08-02).
//
// Problem class: MSAL's `useIsAuthenticated` reflects ACCOUNT
// PRESENCE in the cache, not token validity. When the External ID
// session dies (refresh token expired or revoked) the account
// object survives in sessionStorage, so /app's RequireAuth admits
// the user AND /login's already-signed-in effect bounces them
// away from the sign-in form before it renders. The two pages
// ping-pong off the same stale account and the only
// user-discoverable recovery was closing the tab (sessionStorage
// wipe). These helpers give every surface a real recovery path:
//
//   probeExternalIdSession   — asks MSAL for an API token
//     silently; the definitive is-this-session-actually-alive
//     check behind /login's auto-redirect.
//   clearExternalIdAccounts  — evicts External ID accounts from
//     the MSAL cache so account-presence checks read signed-out.
//   redirectToLogin          — full-page navigation to /login
//     carrying the current in-app location as ?returnTo=, plus a
//     one-shot session-expired flag so /login can explain WHY the
//     user is back on the form.
//   consumeSessionExpiredFlag — reads + clears that flag.
//   clearStuckMsalInteraction — removes MSAL's persisted
//     interaction-status flag after an interrupted redirect (the
//     state where every later loginRedirect throws
//     interaction_in_progress — the "Sign-in could not start"
//     dead end).
//   isInteractionInProgressError — classifier for that error.
//
// Fail direction (Gotcha #84 discipline): the PROBE fails toward
// "alive" — only a definitive InteractionRequiredAuthError marks
// the session stale. A transient or network failure must never
// destroy a working session; the worst case of failing-alive is
// landing on the bootstrap error screen, which offers Retry.
//
// Scope: External ID (browser surface) only. The Teams surface
// never reaches these paths (Teams SSO short-circuits MSAL in
// api.js), and the federated instance has its own redirect-based
// recovery (api.js getAccessToken, federated branch).
// ============================================================

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest, isExternalIdAccount } from './authConfig';

const SESSION_EXPIRED_FLAG = 'carehub.sessionExpired';

/**
 * Silently probe whether the External ID session can still mint
 * an API token. Returns one of:
 *   'alive'      — token acquired (or failure was non-definitive)
 *   'stale'      — MSAL says interaction is required: the cached
 *                  account's tokens are dead
 *   'no-account' — no External ID account in the cache (nothing
 *                  to probe; e.g. a federated-only session)
 *
 * Side benefit: a successful probe warms the MSAL token cache for
 * the API scope, so the app's first real call hits cache.
 *
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance
 * @returns {Promise<'alive'|'stale'|'no-account'>}
 */
export async function probeExternalIdSession(instance) {
  const accounts = instance.getAllAccounts().filter(isExternalIdAccount);
  if (accounts.length === 0) return 'no-account';
  try {
    await instance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
    return 'alive';
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) return 'stale';
    // Transient/network/unknown failure — fail toward preserving
    // the session (see header). Never evict on an ambiguous error.
    return 'alive';
  }
}

/**
 * Evict every External ID account from the MSAL cache (federated
 * accounts, sharing the same physical cache, are untouched —
 * accounts are environment-filtered, never cleared wholesale).
 * Best-effort: a cache-clear failure is swallowed; the caller's
 * navigation still lands the user somewhere recoverable.
 *
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance
 */
export async function clearExternalIdAccounts(instance) {
  const accounts = instance.getAllAccounts().filter(isExternalIdAccount);
  for (const account of accounts) {
    try {
      if (typeof instance.clearCache === 'function') {
        // clearCache is local-only — clears tokens + account from
        // the browser cache without navigating to the AAD
        // end-session endpoint (a dead session there can strand
        // the user on an empty account picker).
        await instance.clearCache({ account });
      }
    } catch { /* best-effort — see doc comment */ }
  }
}

/**
 * Full-page navigation to /login, carrying the current location
 * (or an explicit one) as ?returnTo= so sign-in returns the user
 * to where they were. `sessionExpired: true` sets a one-shot
 * sessionStorage flag that /login consumes to show a "your
 * session has expired" notice regardless of which surface
 * detected the expiry.
 *
 * Deliberately a hard navigation (window.location.assign), not a
 * router navigate — callers are outside the router (api.js) or
 * mid-teardown, and a fresh document load also resets any app
 * state the dead session left behind.
 */
export function redirectToLogin({ pathname, search, sessionExpired = false } = {}) {
  if (sessionExpired) {
    try {
      window.sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
    } catch { /* private mode / storage disabled — notice is cosmetic */ }
  }
  const path = (typeof pathname === 'string' && pathname.length > 0)
    ? pathname
    : window.location.pathname;
  const qs = (typeof search === 'string') ? search : window.location.search;
  window.location.assign(`/login?returnTo=${encodeURIComponent(path + qs)}`);
}

/**
 * Read + clear the one-shot session-expired flag set by
 * redirectToLogin. Returns true exactly once per flag set.
 */
export function consumeSessionExpiredFlag() {
  try {
    const set = window.sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
    if (set) window.sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    return set;
  } catch {
    return false;
  }
}

/**
 * Remove MSAL's persisted interaction-status entries from
 * sessionStorage. MSAL sets an interaction flag when a redirect
 * starts and clears it when the round-trip completes; an
 * interrupted redirect (user navigated away mid-auth) leaves the
 * flag set, after which every loginRedirect/acquireTokenRedirect
 * throws BrowserAuthError interaction_in_progress until the flag
 * is cleared or the tab is closed. Key names are version-prefixed
 * (msal.{clientId}.interaction.status in v3+), so match on the
 * stable 'interaction.status' fragment rather than exact names.
 *
 * Returns true when at least one entry was removed — callers use
 * this to decide whether a retry is worthwhile.
 */
export function clearStuckMsalInteraction() {
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.includes('interaction.status')) doomed.push(key);
    }
    doomed.forEach((key) => window.sessionStorage.removeItem(key));
    return doomed.length > 0;
  } catch {
    return false;
  }
}

/**
 * Classifier for MSAL's interaction_in_progress error. Matched on
 * errorCode rather than instanceof BrowserAuthError so it is
 * robust across msal-browser versions (the code string is the
 * stable contract).
 */
export function isInteractionInProgressError(err) {
  return !!err && err.errorCode === 'interaction_in_progress';
}
